// SachiSkin NDA Signing Portal - Complete Application
// Consolidated JavaScript with all functionality

// ============================================
// 1. SIGNATURE PAD COMPONENT
// ============================================
class SignaturePad {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.isDrawing = false;
    this.hasSignature = false;
    this.lastX = 0;
    this.lastY = 0;
    
    this.setupCanvas();
    this.bindEvents();
  }
  
  setupCanvas() {
    // Set canvas resolution for high DPI displays
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    
    this.canvas.style.width = rect.width + 'px';
    this.canvas.style.height = rect.height + 'px';
    this.canvas.style.cursor = 'crosshair';
    this.canvas.style.touchAction = 'none';
    
    this.ctx.scale(dpr, dpr);
    
    // Drawing properties
    this.ctx.strokeStyle = '#2A2F27';
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    
    this.drawSignatureLine();
    
    console.log('Canvas setup:', { width: this.canvas.width, height: this.canvas.height, dpr });
  }
  
  drawSignatureLine() {
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    this.ctx.save();
    this.ctx.strokeStyle = '#C7D1C0';
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([5, 5]);
    this.ctx.beginPath();
    this.ctx.moveTo(40, height - 20);
    this.ctx.lineTo(width - 40, height - 20);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
    this.ctx.restore();
  }
  
  bindEvents() {
    this.canvas.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.canvas.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.canvas.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.canvas.addEventListener('mouseleave', this.handleMouseUp.bind(this));
    this.canvas.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.canvas.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.canvas.addEventListener('touchend', this.handleTouchEnd.bind(this));
  }
  
  handleMouseDown(e) {
    console.log('Mouse down:', e.clientX, e.clientY);
    this.isDrawing = true;
    this.hasSignature = true;
    const rect = this.canvas.getBoundingClientRect();
    this.lastX = e.clientX - rect.left;
    this.lastY = e.clientY - rect.top;
    console.log('Start position:', this.lastX, this.lastY);
  }
  
  handleMouseMove(e) {
    if (!this.isDrawing) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    this.drawLine(this.lastX, this.lastY, x, y);
    this.lastX = x;
    this.lastY = y;
  }
  
  handleMouseUp() {
    this.isDrawing = false;
  }
  
  handleTouchStart(e) {
    e.preventDefault();
    const touch = e.touches[0];
    this.isDrawing = true;
    this.hasSignature = true;
    const rect = this.canvas.getBoundingClientRect();
    this.lastX = touch.clientX - rect.left;
    this.lastY = touch.clientY - rect.top;
  }
  
  handleTouchMove(e) {
    e.preventDefault();
    if (!this.isDrawing) return;
    const touch = e.touches[0];
    const rect = this.canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    this.drawLine(this.lastX, this.lastY, x, y);
    this.lastX = x;
    this.lastY = y;
  }
  
  handleTouchEnd() {
    this.isDrawing = false;
  }
  
  drawLine(x1, y1, x2, y2) {
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
  }
  
  clear() {
    const rect = this.canvas.getBoundingClientRect();
    this.ctx.clearRect(0, 0, rect.width, rect.height);
    this.drawSignatureLine();
    this.hasSignature = false;
    console.log('Canvas cleared');
  }
  
  reinitialize() {
    console.log('Reinitializing canvas...');
    
    let retryCount = 0;
    const maxRetries = 100; // 5 seconds max
    
    // Wait for canvas to have actual dimensions
    const checkAndSetup = () => {
      const rect = this.canvas.getBoundingClientRect();
      console.log('Checking canvas dimensions:', rect.width, rect.height, 'retry:', retryCount);
      
      if (rect.width > 0 && rect.height > 0) {
        this.setupCanvas();
        console.log('Canvas reinitialized with dimensions:', rect.width, rect.height);
      } else if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(checkAndSetup, 50);
      } else {
        // Fallback: use HTML attributes
        console.warn('Canvas dimensions still 0 after retries, using HTML attributes as fallback');
        const htmlWidth = parseInt(this.canvas.getAttribute('width')) || 600;
        const htmlHeight = parseInt(this.canvas.getAttribute('height')) || 200;
        
        // Force dimensions via CSS
        this.canvas.style.width = htmlWidth + 'px';
        this.canvas.style.height = htmlHeight + 'px';
        
        // Try setup one more time
        setTimeout(() => {
          const newRect = this.canvas.getBoundingClientRect();
          console.log('After CSS force, dimensions:', newRect.width, newRect.height);
          if (newRect.width > 0) {
            this.setupCanvas();
          } else {
            console.error('Canvas still has no dimensions. Check CSS for .signature-pad-container and #signature-pad');
          }
        }, 100);
      }
    };
    
    checkAndSetup();
  }
  
  isEmpty() {
    return !this.hasSignature;
  }
  
  toDataURL() {
    return this.canvas.toDataURL('image/png');
  }
}

// ============================================
// 2. PDF GENERATOR
// ============================================
class NDAPDFGenerator {
  constructor() {
    this.jsPDF = window.jspdf.jsPDF;
    
    // Farah's signature (pre-loaded)
    this.farahSignatureData = 'data:image/png;base64,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';
  }
  
  async generatePDF(formData, signatureDataURL) {
    const pdf = new this.jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = margin;
    
    // Helper function to check if we need a new page
    const checkPageBreak = (spaceNeeded) => {
      if (yPosition + spaceNeeded > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };
    
    // Header
    pdf.setFillColor(0, 60, 70);
    pdf.rect(0, 0, pageWidth, 40, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.text('NON-DISCLOSURE AGREEMENT', pageWidth / 2, 25, { align: 'center' });
    
    yPosition = 55;
    pdf.setTextColor(42, 47, 39);
    
    // Date
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    pdf.text(`Dated: ${currentDate}`, margin, yPosition);
    yPosition += 12;
    
    // Between Section
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BETWEEN', margin, yPosition);
    yPosition += 8;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(formData.fullName, margin, yPosition);
    yPosition += 5;
    pdf.text(formData.company, margin, yPosition);
    yPosition += 5;
    const addressLines = pdf.splitTextToSize(formData.homeAddress, contentWidth);
    pdf.text(addressLines, margin, yPosition);
    yPosition += (addressLines.length * 5) + 8;
    
    pdf.setFont('helvetica', 'bold');
    pdf.text('AND', margin, yPosition);
    yPosition += 8;
    
    pdf.setFont('helvetica', 'normal');
    pdf.text('SachiSkin Pte. Ltd. (UEN no.: 201709582M)', margin, yPosition);
    yPosition += 5;
    pdf.text('7500A Beach Road #04-326 The Plaza Singapore 199591', margin, yPosition);
    yPosition += 10;
    
    // Full NDA Content (matching mutual Nda Sachi skin.md)
    const addSection = (title, content, bold = true) => {
      checkPageBreak(30);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.text(title, margin, yPosition);
      yPosition += 6;
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const lines = pdf.splitTextToSize(content, contentWidth);
      lines.forEach(line => {
        checkPageBreak(6);
        pdf.text(line, margin, yPosition);
        yPosition += 4.5;
      });
      yPosition += 3;
    };
    
    addSection('', 'Relating to a collaboration between the Parties in connection with the branding, creative direction, photography, development, registration, retailing, operations, financial management, business analysis, reporting, reselling, administration and/or manufacturing of cosmetic products formulated for SachiSkin for the commercialization of such products by SachiSkin ("the Field").');
    
    addSection('WHEREAS', 'The Parties agree to collaborate with each other to develop, produce, manufacture, market and support the go to market of cosmetic products. In order to launch cosmetic products for SachiSkin there will be an exchange of research and work conducted by SachiSkin which is to be further developed for the commercialization of such products by SachiSkin. It may be necessary for each of the Parties to disclose certain information on a confidential basis, all of which is regarded as a commercial asset of considerable value.');
    
    addSection('THEREFORE', 'In consideration of the above the Parties agree to be legally bound as follows:');
    
    addSection('1. DEFINITIONS', 'For the purposes of this Mutual Non-Disclosure Agreement ("this Agreement") the following definitions shall apply:');
    
    addSection('1.1', '"Information" means all information, including all oral and visual information, and all information recorded in writing or electronically, or in material form including without limitation models, prototypes, moulds, samples, or in any other medium or by any other method, or which can be ascertained by inspection or analysis of any of the foregoing.');
    
    addSection('1.2', '"Confidential Information" means all Information relating to the Field which is disclosed by or on behalf of any Party to the other Party or which is created in the course of the Project, including without limitation all information relating to the operations, manuals, processes, plans, intentions, product information, product formulae, scientific research, know-how, designs, photography ideation, creative ideations, trade secrets, software, market opportunities, financial condition, business affairs, employees, suppliers and customers of each Party.');
    
    addSection('1.3', '"Disclosing Party" means the Party which disclosed the Confidential Information.');
    
    addSection('1.4', '"Project" means the collaborative project by the Parties to develop, financial forecast and support the launch and registration of cosmetic products for the commercialization of such products by SachiSkin, sourcing suppliers, regulatory checks on ingredients, safety analysis, analyzing, selecting ingredients, product and creative videos, photography shooting, marketing campaign creation and composition of the complete and commercially viable formulation for manufacturing.');
    
    addSection('1.5', '"Receiving Party" means the Party which received the Confidential Information.');
    
    addSection('2. OBLIGATION OF NON-DISCLOSURE', 'The obligation of non-disclosure under this Agreement shall continue unless and until the Confidential Information enters the public domain other than as a result of a breach of this or any other agreement. Upon request of the Disclosing Party, all the Disclosing Party\'s Confidential Information in whatever form and to any extent in the possession of the Receiving Party shall be returned to the Disclosing Party, or at the option and written instruction of the Disclosing Party, destroyed.');
    
    addSection('3. OBLIGATIONS OF RECEIVING PARTY', 'Each Party agrees that:');
    
    addSection('3.1', 'Any Confidential Information disclosed by the Disclosing Party hereunder shall be used by the Receiving Party solely for the Field and for no other purpose.');
    
    addSection('3.2', 'Any Confidential Information disclosed by the Disclosing Party ("the Disclosing Party\'s Confidential Information") hereunder shall remain at all times the property of the Disclosing Party.');
    
    addSection('3.3', 'Any Confidential Information created in the course of the Project shall belong to SachiSkin.');
    
    addSection('3.4', 'Except as may be required by applicable laws or legal process, it will not allow distribution or disclosure of any Confidential Information to any third party and will only allow access to the Confidential Information to its own employees who have a need to know such Confidential Information for the purpose for which it is disclosed.');
    
    addSection('3.5', 'The obligations of non-disclosure hereunder do not apply to a Party if: (a) The same Information as the Confidential Information is generally available to the public other than as a result of a breach of this or any other Agreement; or (b) The Party proves that the same Information as Confidential Information is already in the possession of the Party without restriction and prior to the Project or any disclosure hereunder; or (c) The Party proves that the same Information as the Confidential Information is or has been lawfully disclosed to that Party by a third party, not employed by or otherwise affiliated with the Receiving Party, who is lawfully entitled to disclose the same; or (d) The Party proves that the same Information as the Confidential Information is or has been independently developed by that Party and no Confidential Information disclosed hereunder or created under the Project has been used directly or indirectly; or (f) All the Parties give their prior written consent to the distribution or disclosure of Confidential Information created in the course of the Project.');
    
    addSection('3.6', 'Copies of the Confidential Information shall only be made as strictly necessary with prior consent and such copying or storage of the Confidential Information shall not be performed outside the usual place of business.');
    
    addSection('4. SAFEGUARDS', 'Each Party warrants to the other that it has in place reasonable safeguards against the unauthorized disclosure of the Confidential Information such that it can comply with the provisions of this Agreement and agrees that without prejudice to the foregoing it shall protect the Confidential Information in at least the same manner and to at least the same degree that it protects its own confidential information. Each Party further agrees to inform its employees having access to Confidential Information of the obligations hereunder.');
    
    addSection('5. CONFIDENTIALITY OF AGREEMENT', 'Except as may be required by law or the Listing Manual of the Singapore Exchange, the Parties agree not to disclose to any person either the fact that discussions or negotiations are taking place concerning a transaction or any of the terms, conditions or other relevant facts without the prior written consent of the other. The existence of this Agreement between the Parties concerning the Confidential Information is confidential.');
    
    addSection('6. REMEDIES', 'Each Party agrees that money damages may not be a sufficient remedy for any breach of this Agreement and that the other Party shall be entitled to seek injunctive or other equitable relief to remedy or prevent any breach or threatened breach of this Agreement by the first mentioned Party. Such remedy shall not be the exclusive remedy for any breach of this Agreement, but shall be in addition to all other rights and remedies available at law.');
    
    addSection('7. NO FAILURE OR DELAY', 'No failure or delay by either party in exercising any right, power or privilege, partial or otherwise, shall operate as a waiver thereof.');
    
    addSection('8. PRIOR INFORMATION', 'Any information supplied to the Receiving Party and those working with the Receiving Party and designated as confidential at the time of disclosure by the Disclosing Party and any information created in the course of the Project prior to the execution of this Agreement shall be considered in the same manner and be subject to the same treatment as the Confidential Information made available after the execution of this Agreement.');
    
    addSection('9. NO OBLIGATION TO PROCEED', 'It is understood that this Agreement is not intended to, and does not, obligate the Parties to enter into any further agreements or to proceed with any possible business relationship or other transaction.');
    
    addSection('10. NO LICENSE GRANTED', 'Nothing contained in this Agreement shall be construed as granting or conferring rights by license or otherwise in any Confidential Information disclosed to any Party except solely for the purposes of this Agreement.');
    
    addSection('11. INTELLECTUAL PROPERTY RIGHTS', 'The Parties agree that:');
    
    addSection('11.1', 'The Disclosing Party retains all intellectual property rights in the Disclosing Party\'s Confidential Information at all times and for all purposes including copyright in any materials produced by the Receiving Party relating to the Confidential Information and the right to seek and obtain patent or similar protection anywhere in the world in respect of any invention comprised or referred to in the Confidential Information.');
    
    addSection('11.2', 'SachiSkin shall own all intellectual property rights in the Confidential Information created in the course of the Project and for all purposes including copyright in any materials produced by either Party relating to the Confidential Information and the right to seek and obtain patent or similar protection anywhere in the world in respect of any invention comprised or referred to in the Confidential Information.');
    
    addSection('11.3', 'The Parties agree to execute all necessary documents and take all necessary steps to give effect to this Clause.');
    
    addSection('12. ASSIGNMENT', 'SachiSkin shall be entitled, without the prior written consent of the Receiving Party, to assign, transfer or novate all or some of its rights and obligations under this Agreement to its related company or its affiliates. The Parties agree to execute the necessary documents as required by SachiSkin to effect such an assignment or transfer or novation (as the case may be).');
    
    addSection('13. AMENDMENTS', 'Any amendment to this Agreement shall be in writing and signed by an authorized officer of each of the Parties.');
    
    addSection('14. GOVERNING LAW', 'This Agreement shall be governed by the laws of the Republic of Singapore. The Parties hereby agree to submit to the non-exclusive jurisdiction of the courts of Singapore.');
    
    // Signature section - Two columns
    checkPageBreak(80);
    yPosition += 10;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('SIGNATURES', margin, yPosition);
    yPosition += 10;
    
    // Two-column layout
    const leftCol = margin;
    const rightCol = margin + 95;
    
    // Left column - Receiving Party (User's signature)
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Receiving Party:', leftCol, yPosition);
    yPosition += 5;
    
    if (signatureDataURL) {
      try {
        pdf.addImage(signatureDataURL, 'PNG', leftCol, yPosition, 60, 25);
      } catch (err) {
        console.error('Error adding user signature:', err);
      }
    }
    
    const signatureY = yPosition + 30;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(leftCol, signatureY, leftCol + 80, signatureY);
    
    let textY = signatureY + 5;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text(formData.fullName, leftCol, textY);
    textY += 4;
    pdf.text(formData.company, leftCol, textY);
    textY += 4;
    pdf.text(formData.email, leftCol, textY);
    textY += 4;
    pdf.text(currentDate, leftCol, textY);
    
    // Right column - Disclosing Party (Farah's signature image)
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Disclosing Party (SachiSkin):', rightCol, yPosition - 5);
    
    // Add Farah's signature image
    try {
      // Use the pre-loaded PNG signature data
      // Dimensions: 72mm × 19mm (matches 272×71 pixel aspect ratio)
      pdf.addImage(this.farahSignatureData, 'PNG', rightCol, yPosition, 72, 19);
      console.log('Farah signature added successfully');
    } catch (err) {
      console.error('Error adding Farah signature to PDF:', err);
      // Fallback to styled text if image fails
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'italic');
      pdf.text('Farah Bashir', rightCol, yPosition + 12);
    }
    
    pdf.setDrawColor(200, 200, 200);
    pdf.line(rightCol, signatureY, rightCol + 80, signatureY);
    
    textY = signatureY + 5;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Farah Bashir', rightCol, textY);
    textY += 4;
    pdf.text('Founder', rightCol, textY);
    textY += 4;
    pdf.text('SachiSkin Pte. Ltd.', rightCol, textY);
    textY += 4;
    pdf.text(currentDate, rightCol, textY);
    
    yPosition = Math.max(textY, signatureY + 30) + 10;
    
    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text('This document is electronically signed and legally binding.', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    return pdf;
  }
  
  downloadPDF(pdf, filename) {
    pdf.save(filename);
  }
  
  getPDFBlob(pdf) {
    return pdf.output('blob');
  }
}

// ============================================
// 3. API SERVICE
// ============================================
class NDAAPIService {
  constructor(edgeFunctionURL, anonKey = null) {
    this.edgeFunctionURL = edgeFunctionURL;
    this.anonKey = anonKey;
  }
  
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (this.anonKey) {
      headers['Authorization'] = `Bearer ${this.anonKey}`;
    }
    
    return headers;
  }
  
  async uploadNDAToShopify(pdfBlob, formData, filename) {
    try {
      console.log('Step 1: Converting PDF to base64...');
      
      // Convert PDF blob to base64
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1]; // Remove data:application/pdf;base64, prefix
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });
      
      console.log('Step 2: Uploading to Supabase Storage...');
      
      // Upload to Supabase Storage
      const uploadResponse = await fetch(this.edgeFunctionURL, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          action: 'uploadPDF',
          pdfBase64: pdfBase64,
          filename: filename,
          fullName: formData.fullName,
          email: formData.email,
          company: formData.company,
          homeAddress: formData.homeAddress
        }),
      });
      
      if (!uploadResponse.ok) {
        const error = await uploadResponse.json();
        throw new Error(error.message || 'Failed to upload PDF');
      }
      
      const uploadData = await uploadResponse.json();
      
      // Step 3: Track to Klaviyo using client-side _learnq (if available)
      console.log('Step 3: Tracking to Klaviyo...');
      
      let klaviyoUpdated = false;
      
      try {
        if (typeof window._learnq !== 'undefined') {
          // Identify user with profile properties
          window._learnq.push(['identify', {
            '$email': formData.email,
            'nda_signed': true,
            'nda_url': uploadData.fileUrl,
            'nda_signed_date': new Date().toISOString(),
            'company': formData.company,
            'full_name': formData.fullName
          }]);
          
          // Track NDA completion event
          window._learnq.push(['track', 'NDA Form Completed', {
            $email: formData.email,
            nda_url: uploadData.fileUrl,
            company: formData.company,
            home_address: formData.homeAddress,
            signed_date: new Date().toISOString(),
            full_name: formData.fullName
          }]);
          
          klaviyoUpdated = true;
          console.log('✓ Klaviyo tracking sent successfully');
        } else {
          console.warn('Klaviyo (_learnq) not loaded, skipping tracking');
        }
      } catch (e) {
        console.warn('Klaviyo tracking failed (non-critical):', e);
      }
      
      console.log('✓ Upload complete');
      
      return {
        fileUrl: uploadData.fileUrl,
        klaviyoUpdated,
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  }
}

// ============================================
// 4. MAIN APPLICATION
// ============================================
class NDAApp {
  constructor() {
    this.currentStep = 'welcome';
    this.signaturePad = null;
    this.pdfGenerator = null;
    this.apiService = null;
    this._pendingPdf = null;
    this._pendingFilename = null;
    
    // IMPORTANT: Replace with your Supabase credentials
    this.edgeFunctionURL = 'https://ejhsttpvnyqmeziyrvjn.supabase.co/functions/v1/nda-supabase-handler';
    this.supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVqaHN0dHB2bnlxbWV6aXlydmpuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIwNzU2OTUsImV4cCI6MjA2NzY1MTY5NX0.NjopauGkpEbnJNw8GEVG-t8zCzBHrUyNGg-bmpNwhEA';
    
    this.init();
  }
  
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  setup() {
    const canvas = document.getElementById('signature-pad');
    if (canvas) {
      this.signaturePad = new SignaturePad(canvas);
      
      // Add event listeners to canvas for signature drawing
      canvas.addEventListener('mouseup', () => this.validateForm());
      canvas.addEventListener('touchend', () => this.validateForm());
    }
    
    this.pdfGenerator = new NDAPDFGenerator();
    
    // Pass anon key to API service if not placeholder
    const anonKey = this.supabaseAnonKey !== 'YOUR_SUPABASE_ANON_KEY_HERE' ? this.supabaseAnonKey : null;
    this.apiService = new NDAAPIService(this.edgeFunctionURL, anonKey);
    
    this.bindEvents();
    this.showSection('welcome');
    
    console.log('NDA App: Setup complete');
  }
  
  bindEvents() {
    // Welcome section
    const readNDABtn = document.getElementById('read-nda-btn');
    const cancelBtn = document.getElementById('cancel-btn');
    
    if (readNDABtn) readNDABtn.addEventListener('click', () => this.openNDAModal());
    if (cancelBtn) cancelBtn.addEventListener('click', () => window.location.href = '/');
    
    // NDA Modal
    const agreeNDABtn = document.getElementById('agree-nda-btn');
    const modalClose = document.querySelector('.modal-close');
    const modalOverlay = document.querySelector('.modal-overlay');
    
    if (agreeNDABtn) agreeNDABtn.addEventListener('click', () => this.agreeNDA());
    if (modalClose) modalClose.addEventListener('click', () => this.closeNDAModal());
    if (modalOverlay) modalOverlay.addEventListener('click', () => this.closeNDAModal());
    
    // Form section
    const form = document.getElementById('nda-form');
    const clearSignatureBtn = document.getElementById('clear-signature');
    
    if (form) {
      form.addEventListener('submit', (e) => this.handleFormSubmit(e));
      form.addEventListener('input', () => this.validateForm());
    }
    
    if (clearSignatureBtn && this.signaturePad) {
      clearSignatureBtn.addEventListener('click', () => {
        this.signaturePad.clear();
        this.validateForm();
      });
    }
    
    // Success section
    const downloadNdaBtn = document.getElementById('download-nda-btn');
    if (downloadNdaBtn) downloadNdaBtn.addEventListener('click', () => this.downloadPendingPDF());

    const signAnotherBtn = document.getElementById('sign-another-btn');
    if (signAnotherBtn) signAnotherBtn.addEventListener('click', () => this.startOver());
  }
  
  showSection(sectionName) {
    console.log('NDA App: Showing section:', sectionName);
    
    const sections = document.querySelectorAll('.nda-section');
    sections.forEach(section => {
      section.classList.remove('active');
      section.style.display = 'none';
    });
    
    const activeSection = document.getElementById(`${sectionName}-section`);
    if (activeSection) {
      activeSection.classList.add('active');
      activeSection.style.display = 'block';
      console.log('NDA App: Section displayed:', sectionName);
      
      // Reinitialize signature pad when showing form
      if (sectionName === 'form' && this.signaturePad) {
        // Use requestAnimationFrame to ensure DOM has rendered
        requestAnimationFrame(() => {
          const canvas = document.getElementById('signature-pad');
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          canvas.getContext('2d').scale(ratio, ratio);
          this.signaturePad.clear();
        });
      }
    } else {
      console.error('NDA App: Section not found:', sectionName);
    }
    
    this.currentStep = sectionName;
  }
  
  openNDAModal() {
    const modal = document.getElementById('nda-modal');
    if (modal) {
      modal.classList.add('active');
      modal.style.display = 'block';
      document.body.style.overflow = 'hidden';
    }
  }
  
  closeNDAModal() {
    const modal = document.getElementById('nda-modal');
    if (modal) {
      modal.classList.remove('active');
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
  }
  
  agreeNDA() {
    this.closeNDAModal();
    this.showSection('form');
  }
  
  validateForm() {
    const form = document.getElementById('nda-form');
    const submitBtn = document.getElementById('submit-btn');
    const statusDiv = document.getElementById('form-status');
    
    if (!form || !submitBtn) return;
    
    const fullName = form.elements['fullName'].value.trim();
    const company = form.elements['company'].value.trim();
    const email = form.elements['email'].value.trim();
    const homeAddress = form.elements['homeAddress'].value.trim();
    const hasSignature = this.signaturePad && !this.signaturePad.isEmpty();
    
    const isValid = fullName && email && email.includes('@') && homeAddress && hasSignature;
    
    submitBtn.disabled = !isValid;
    
    if (isValid) {
      statusDiv.innerHTML = `
        <svg class="icon icon-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        All fields completed. Ready to sign.
      `;
      statusDiv.className = 'form-status success';
    } else {
      statusDiv.innerHTML = '';
      statusDiv.className = 'form-status';
    }
  }
  
  async handleFormSubmit(e) {
    e.preventDefault();
    
    const form = e.target;
    const submitBtn = document.getElementById('submit-btn');
    const submitText = document.getElementById('submit-text');
    const statusDiv = document.getElementById('form-status');
    
    const formData = {
      fullName: form.elements['fullName'].value.trim(),
      company: form.elements['company'].value.trim(),
      email: form.elements['email'].value.trim(),
      homeAddress: form.elements['homeAddress'].value.trim(),
    };
    
    const signatureDataURL = this.signaturePad.toDataURL();
    
    submitBtn.disabled = true;
    submitText.textContent = 'Preparing...';
    statusDiv.innerHTML = '<svg class="icon icon-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg> Preparing your agreement...';
    statusDiv.className = 'form-status loading';
    
    try {
      // Generate PDF (kept in memory — no auto-download)
      const pdf = await this.pdfGenerator.generatePDF(formData, signatureDataURL);
      const pdfBlob = this.pdfGenerator.getPDFBlob(pdf);

      // Generate filename: use company name if provided, otherwise fall back to full name
      const filenameIdentifier = (formData.company && formData.company.trim())
        ? formData.company.trim()
        : formData.fullName;
      const filename = `Sachi Skin Non-Disclosure Agreement (NDA) ${filenameIdentifier}.pdf`;

      // Store for manual download on the success screen
      this._pendingPdf = pdf;
      this._pendingFilename = filename;

      // Submit to server — block UI until the backend fully confirms
      submitText.textContent = 'Submitting...';
      statusDiv.innerHTML = '<svg class="icon icon-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg> Submitting your agreement — please do not close this page...';
      statusDiv.className = 'form-status loading';

      const { fileUrl, klaviyoUpdated } = await this.apiService.uploadNDAToShopify(pdfBlob, formData, filename);

      statusDiv.innerHTML = '<svg class="icon icon-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg> Submitted successfully!';
      statusDiv.className = 'form-status success';

      setTimeout(() => this.showSection('success'), 1000);
      
    } catch (error) {
      console.error('Error:', error);
      
      statusDiv.innerHTML = `<svg class="icon icon-error" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg> ${error.message || 'An error occurred. Please try again.'}`;
      statusDiv.className = 'form-status error';
      
      submitBtn.disabled = false;
      submitText.textContent = 'Sign & Submit NDA';
    }
  }
  
  startOver() {
    const form = document.getElementById('nda-form');
    if (form) form.reset();
    if (this.signaturePad) this.signaturePad.clear();
    this._pendingPdf = null;
    this._pendingFilename = null;
    this.showSection('welcome');
  }

  downloadPendingPDF() {
    if (this._pendingPdf && this._pendingFilename) {
      this.pdfGenerator.downloadPDF(this._pendingPdf, this._pendingFilename);
    }
  }
}

// Initialize app
if (typeof window !== 'undefined') {
  console.log('NDA App: Initializing...');
  window.ndaApp = new NDAApp();
}
